import React, { memo, useMemo, useEffect } from 'react';
import { Map, APIProvider, Marker, useMap } from '@vis.gl/react-google-maps';
import { Map as MapIcon } from 'lucide-react';
import { ITrip } from '../../../../Models/trips/trip.model';
import ActivityTypeHelper, {
  ActivityType
} from '../../../../types/activity-types';
import ItinerariesSvg from '../../../itineraries/itineraries-svg';
import { renderToStaticMarkup } from 'react-dom/server';
import config from '../../../../config';
import './overview-panels.scss';

interface TripMapOverviewProps {
  trip: ITrip;
}

interface MarkerInfo {
  id: string;
  lat: number;
  lng: number;
  type?: string;
  name: string;
}

const ZOOM_LIMITS = {
  MIN: 1,
  MAX_AFTER_FIT: 14,
  DEFAULT: 8
} as const;

const MARKER_SIZE = 24;

const TripMapOverview: React.FC<TripMapOverviewProps> = ({ trip }) => {
  // Collect all markers from activities
  const markers: MarkerInfo[] = useMemo(() => {
    const activityMarkers: MarkerInfo[] = [];

    // Add markers from unbooked activities
    if (trip.activities) {
      trip.activities.forEach((activity) => {
        if (!activity.deleted && activity.latitude && activity.longitude) {
          activityMarkers.push({
            id: activity.id?.toString() || '',
            lat: activity.latitude,
            lng: activity.longitude,
            type: activity.type,
            name: activity.title || 'Activity'
          });
        }
      });
    }

    // Add markers from itinerary activities
    if (trip.itineraries) {
      trip.itineraries.forEach((itinerary) => {
        if (!itinerary.deleted) {
          itinerary.days?.forEach((day) => {
            day.activities?.forEach((activity) => {
              if (
                !activity.deleted &&
                activity.latitude &&
                activity.longitude
              ) {
                activityMarkers.push({
                  id: activity.activityId || '',
                  lat: activity.latitude,
                  lng: activity.longitude,
                  type: activity.type,
                  name: activity.title || 'Activity'
                });
              }
            });
          });
        }
      });
    }

    return activityMarkers;
  }, [trip]);

  // Calculate center position and bounds
  const { centerPosition, bounds } = useMemo(() => {
    if (markers.length === 0) {
      return {
        centerPosition: { lat: 40.7128, lng: -74.006 },
        bounds: null
      };
    }

    if (markers.length === 1) {
      return {
        centerPosition: { lat: markers[0].lat, lng: markers[0].lng },
        bounds: null
      };
    }

    // Calculate bounds with padding from all markers
    const lats = markers.map((m) => m.lat);
    const lngs = markers.map((m) => m.lng);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const latPadding = (maxLat - minLat) * 0.15;
    const lngPadding = (maxLng - minLng) * 0.15;

    return {
      centerPosition: {
        lat: (minLat + maxLat) / 2,
        lng: (minLng + maxLng) / 2
      },
      bounds: {
        south: minLat - latPadding,
        west: minLng - lngPadding,
        north: maxLat + latPadding,
        east: maxLng + lngPadding
      }
    };
  }, [markers]);

  // Create SVG icon for markers
  const createSvgIcon = (type: ActivityType): string => {
    const symbol = ActivityTypeHelper.getSymbol(type);

    const iconSvg = renderToStaticMarkup(
      <ItinerariesSvg
        type={symbol as ActivityType}
        style={{ width: '100%', height: '100%' }}
        fill='#842234'
      />
    );

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${MARKER_SIZE}" height="${MARKER_SIZE}" viewBox="0 0 ${MARKER_SIZE} ${MARKER_SIZE}">
        <g transform="translate(${MARKER_SIZE * 0.15}, ${MARKER_SIZE * 0.15}) scale(${MARKER_SIZE / 30})">
          ${iconSvg.replace(/<svg[^>]*>/, '').replace('</svg>', '')}
        </g>
      </svg>
    `;

    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  // Get marker icon
  const getMarkerIcon = (type?: string) => {
    if (!window.google?.maps?.Size) {
      return { url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png' };
    }

    const activityType = ActivityTypeHelper.normalizeType(type);
    const url = createSvgIcon(activityType);

    return {
      url,
      scaledSize: new google.maps.Size(MARKER_SIZE, MARKER_SIZE),
      anchor: new google.maps.Point(MARKER_SIZE / 2, MARKER_SIZE / 2)
    };
  };

  const activityCount = markers.length;

  // Inner component to access map instance
  const MapContent: React.FC<{ markers: MarkerInfo[]; bounds: any }> = ({
    markers,
    bounds
  }) => {
    const map = useMap();

    useEffect(() => {
      if (!map || !bounds || markers.length <= 1) return;

      // Fit bounds to show all markers
      const googleBounds = new google.maps.LatLngBounds(
        { lat: bounds.south, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east }
      );
      map.fitBounds(googleBounds);

      // Limit max zoom after fitBounds
      setTimeout(() => {
        const currentZoom = map.getZoom() || 8;
        if (currentZoom > ZOOM_LIMITS.MAX_AFTER_FIT) {
          map.setZoom(ZOOM_LIMITS.MAX_AFTER_FIT);
        }
      }, 100);
    }, [map, bounds, markers.length]);

    return (
      <>
        {markers.map((marker, index) => (
          <Marker
            key={index}
            position={{ lat: marker.lat, lng: marker.lng }}
            icon={getMarkerIcon(marker.type)}
            title={marker.name}
          />
        ))}
      </>
    );
  };

  return (
    <div className='overview-panel-content map-full-height'>
      <div className='map-container-full'>
        {activityCount > 0 ? (
          <APIProvider
            apiKey={config.GOOGLE_MAPS_API_KEY || ''}
            libraries={['places']}
          >
            <Map
              style={{ width: '100%', height: '100%', borderRadius: '6px' }}
              defaultCenter={centerPosition}
              defaultZoom={markers.length === 1 ? 12 : ZOOM_LIMITS.DEFAULT}
              gestureHandling={'greedy'}
              disableDefaultUI={false}
              zoomControl={true}
              clickableIcons={false}
              scrollwheel={true}
              draggable={true}
            >
              <MapContent
                markers={markers}
                bounds={bounds}
              />
            </Map>
          </APIProvider>
        ) : (
          <div className='empty-map'>
            <MapIcon
              size={32}
              className='empty-map-icon'
            />
            <p>No activities with locations yet</p>
            <p className='empty-sub'>Add activities to see them on the map</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(TripMapOverview);
